import React, { useMemo } from 'react';
import { Flex, TextV2, Box } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { StyledGiftProgress, styles } from './GiftProgress.styles';
import { ProgressBar } from '../ProgressBar';
import { Skeleton, SkeletonText, SkeletonGroup } from '@shared/components/Skeleton';
import { pxToRem } from '@withjoy/joykit/theme';

interface GiftProgressProps
  extends Readonly<{
    progressPercentage: number;
    isLoading: boolean;
    title: string;
    currentGiftCount: number;
    targetGiftCount: number;
  }> {}

export const GiftProgress: React.FC<GiftProgressProps> = props => {
  const { progressPercentage, isLoading, title, currentGiftCount, targetGiftCount } = props;

  const { t } = useTranslation('sharedComponents');
  const { progressNotStarted, progressLessThanHalf, halfwayProgress, progressAlmostComplete, progressComplete, giftsAdded } = t('giftProgress');

  const controlledPercentage = useMemo(() => {
    if (progressPercentage < 0) return 0;
    if (progressPercentage > 100) return 100;
    return progressPercentage;
  }, [progressPercentage]);

  const subtitle = useMemo(() => {
    if (controlledPercentage === 0) return progressNotStarted();
    if (controlledPercentage > 0 && controlledPercentage < 50) return progressLessThanHalf();
    if (controlledPercentage > 49 && controlledPercentage < 60) return halfwayProgress();
    if (controlledPercentage > 59 && controlledPercentage < 100) return progressAlmostComplete();
    return progressComplete();
  }, [halfwayProgress, progressAlmostComplete, progressComplete, progressLessThanHalf, progressNotStarted, controlledPercentage]);

  return (
    <StyledGiftProgress __css={styles.giftProgress}>
      <SkeletonGroup
        isReady={!isLoading}
        placeholder={
          <Box backgroundColor="mono1">
            <Flex alignItems="center" justifyContent="space-between" marginBottom={3}>
              <SkeletonText rows={1} skeletonHeight={pxToRem(21)} width={pxToRem(137)} />
              <SkeletonText rows={1} skeletonHeight={pxToRem(18)} width={pxToRem(27)} />
            </Flex>
            <Skeleton marginBottom={3} height={pxToRem(8)} width="100%" />
            <SkeletonText rows={1} skeletonHeight={pxToRem(20)} width={pxToRem(200)} />
          </Box>
        }
      >
        <Flex alignItems="center" justifyContent="space-between">
          <TextV2 typographyVariant="hed1" marginBottom={3}>
            {title}
          </TextV2>
          <TextV2 typographyVariant="label2" marginBottom={3}>
            {currentGiftCount > targetGiftCount ? giftsAdded({ count: currentGiftCount }) : `${currentGiftCount}/${targetGiftCount}`}
          </TextV2>
        </Flex>
        <ProgressBar progressPercentage={progressPercentage} />
        <TextV2 typographyVariant="label2">{subtitle}</TextV2>
      </SkeletonGroup>
    </StyledGiftProgress>
  );
};
